import React from 'react';
import dayjs from 'dayjs';

export function formatMoney(value, currency = 'USD') {
    if (value == null) return '—';
    return `$${Number(value).toFixed(2)}`;
}

export function formatBs(value) {
    if (value == null) return '—';
    return `Bs ${Number(value).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Tooltip for prices converted from bolívares: original amount, BCV rate and its date.
export function priceTooltip(row) {
    if (!row?.converted) return undefined;
    const parts = [
        `Original: ${formatBs(row.originalEffectiveAmount ?? row.originalAmount)}`,
        row.exchangeRate ? `Tasa BCV: Bs ${Number(row.exchangeRate).toFixed(2)} / USD` : null,
        row.exchangeRateDate ? `Fecha tasa: ${dayjs(row.exchangeRateDate).format('DD/MM/YYYY')}` : null,
    ].filter(Boolean);
    return parts.join(' · ');
}

export function buildStoreBreakdownFromTimeline(timeline = []) {
    const grouped = [...timeline
        .reduce((map, obs) => {
            const storeKey =
                obs.storeId && obs.storeId !== 'undefined' && obs.storeId !== 'null'
                    ? obs.storeId
                    : `unknown-${obs.storeCheckId || obs.observedAt}`;
            if (!map.has(storeKey)) {
                map.set(storeKey, {
                    storeId: obs.storeId || storeKey,
                    storeName: obs.storeName || 'PDV sin identificar',
                    items: [],
                });
            }
            map.get(storeKey).items.push(obs);
            return map;
        }, new Map())
        .values()];

    return grouped
        .map((store) => {
            const history = store.items.sort((a, b) => new Date(b.observedAt) - new Date(a.observedAt));
            const latest = history[0];
            return {
                storeId: store.storeId,
                storeName: store.storeName,
                latestPrice: latest.effectivePrice,
                latestObservedAt: latest.observedAt,
                listPrice: latest.listPrice,
                variancePercent: latest.variancePercent,
                observationCount: history.length,
                history: history.map((o) => ({
                    observedAt: o.observedAt,
                    effectivePrice: o.effectivePrice,
                    originalEffectiveAmount: o.originalEffectiveAmount,
                    originalAmount: o.originalEffectiveAmount,
                    originalCurrency: o.originalCurrency,
                    converted: o.converted,
                    exchangeRate: o.exchangeRate,
                    exchangeRateDate: o.exchangeRateDate,
                    variancePercent: o.variancePercent,
                    isPromo: o.isPromo,
                    isAvailable: o.isAvailable,
                    suspect: o.suspect,
                    deviationPercent: o.deviationPercent,
                    decision: o.decision,
                    storeCheckId: o.storeCheckId,
                    productId: o.productId,
                })),
            };
        })
        .sort((a, b) => Math.abs(b.variancePercent ?? 0) - Math.abs(a.variancePercent ?? 0));
}

export function getProductTimeline(product, globalTimeline = []) {
    if (product?.timeline?.length) return product.timeline;
    const productId = product?.productId;
    if (!productId || !globalTimeline.length) return [];

    const normalized = String(productId).toUpperCase();
    return globalTimeline.filter((row) => {
        if (!row?.productId) return false;
        return row.productId === productId || String(row.productId).toUpperCase() === normalized;
    });
}

export function getProductStores(product, globalTimeline = []) {
    if (product?.storeBreakdown?.length) return product.storeBreakdown;

    const timeline = getProductTimeline(product, globalTimeline);
    if (timeline.length) return buildStoreBreakdownFromTimeline(timeline);

    return [];
}

export function getReportingStoreCount(product) {
    return getProductStores(product).length;
}

export function enrichProduct(product, globalTimeline = []) {
    const timeline = getProductTimeline(product, globalTimeline);
    const storeBreakdown = product?.storeBreakdown?.length
        ? product.storeBreakdown
        : buildStoreBreakdownFromTimeline(timeline);

    return {
        ...product,
        timeline,
        storeBreakdown,
        storeCount: storeBreakdown.length,
    };
}

// Δ vs list → tone + label: > +5% danger (above list), < -5% success (below list), else info.
export function varianceMeta(value) {
    if (value == null) return { tone: 'neutral', label: 'Sin datos', trend: 'flat' };
    if (value > 5) return { tone: 'danger', label: `${value > 0 ? '+' : ''}${value}%`, trend: 'up' };
    if (value < -5) return { tone: 'success', label: `${value}%`, trend: 'down' };
    return { tone: 'info', label: `${value > 0 ? '+' : ''}${value}%`, trend: 'flat' };
}

// "1 piso" / "3 pisos" style pluralization used across the explorer copy.
export const plural = (n, one, many = `${one}s`) => (n === 1 ? one : many);

/** Store-name search over a product's store breakdown. */
export function useStoreSearch(stores = []) {
    const [search, setSearch] = React.useState('');

    const filtered = React.useMemo(() => {
        if (!search.trim()) return stores;
        const q = search.trim().toLowerCase();
        return stores.filter((s) => s.storeName?.toLowerCase().includes(q));
    }, [stores, search]);

    return { search, setSearch, filtered };
}
